import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { useQueryClient } from "@tanstack/react-query";
import {
  logout,
  setActiveBusiness,
  type Me,
} from "../lib/auth";

export interface NavSection {
  key: string;
  label: string;
  icon?: string;
}

const SECTIONS: NavSection[] = [
  { key: "overview", label: "Overview", icon: "🏠" },
  { key: "conversations", label: "Conversations", icon: "💬" },
  { key: "leads", label: "Leads", icon: "🎯" },
  { key: "bookings", label: "Bookings", icon: "📅" },
  { key: "handoffs", label: "Handoffs", icon: "🚨" },
  { key: "callbacks", label: "Callbacks", icon: "📞" },
  { key: "billing", label: "Billing", icon: "🧾" },
  { key: "voice-test", label: "Voice test", icon: "🎙" },
  { key: "integrations", label: "Integrations", icon: "🔌" },
  { key: "templates", label: "Email templates", icon: "✉️" },
  { key: "settings", label: "Settings", icon: "⚙️" },
];

interface Props {
  me: Me;
  activeSection: string;
  children: React.ReactNode;
}

export function DashboardShell({ me, activeSection, children }: Props) {
  const [, setLocation] = useLocation();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const qc = useQueryClient();

  async function handleLogout() {
    await logout();
    qc.removeQueries({ queryKey: ["me"] });
    setLocation("/login");
  }

  // Auto-close the mobile drawer on section change so the user lands on the
  // new view instead of staring at the same drawer.
  useEffect(() => {
    setMobileNavOpen(false);
  }, [activeSection]);

  const businessName =
    me.businesses.find((b) => b.id === me.active_business_id)?.name ??
    "(no business selected)";

  return (
    <div className="min-h-screen bg-slate-50">
      <TopBar
        me={me}
        onLogout={handleLogout}
        onMenuClick={() => setMobileNavOpen(true)}
      />
      <div className="flex">
        {/* Desktop sidebar */}
        <aside className="hidden md:block w-60 border-r border-slate-200 bg-white min-h-[calc(100vh-3.5rem)]">
          <SectionNav activeSection={activeSection} businessName={businessName} />
        </aside>
        {/* Mobile drawer */}
        {mobileNavOpen && (
          <div
            className="md:hidden fixed inset-0 z-30 bg-slate-900/40"
            onClick={() => setMobileNavOpen(false)}
          >
            <aside
              className="w-64 max-w-[80vw] bg-white h-full shadow-xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="h-14 flex items-center justify-between px-4 border-b border-slate-200">
                <span className="font-semibold text-slate-900">Menu</span>
                <button
                  onClick={() => setMobileNavOpen(false)}
                  className="text-slate-400 hover:text-slate-600 text-2xl leading-none"
                >
                  ×
                </button>
              </div>
              <SectionNav
                activeSection={activeSection}
                businessName={businessName}
              />
            </aside>
          </div>
        )}
        <main className="flex-1 p-4 md:p-8 max-w-6xl mx-auto w-full">
          {children}
        </main>
      </div>
    </div>
  );
}

function SectionNav({
  activeSection,
  businessName,
}: {
  activeSection: string;
  businessName: string;
}) {
  return (
    <nav className="p-4 space-y-1">
      <div className="px-3 py-2 text-xs uppercase tracking-wider text-slate-500 truncate">
        {businessName}
      </div>
      {SECTIONS.map((s) => (
        <Link
          key={s.key}
          href={`/${s.key}`}
          className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition ${
            activeSection === s.key
              ? "bg-brand-100 text-brand-700"
              : "text-slate-700 hover:bg-slate-100"
          }`}
        >
          <span className="text-base">{s.icon}</span>
          {s.label}
        </Link>
      ))}
    </nav>
  );
}

function TopBar({
  me,
  onLogout,
  onMenuClick,
}: {
  me: Me;
  onLogout: () => void;
  onMenuClick: () => void;
}) {
  return (
    <header className="h-14 bg-white border-b border-slate-200 flex items-center px-3 md:px-6">
      <button
        className="md:hidden mr-2 p-2 rounded-md hover:bg-slate-100 text-slate-700"
        aria-label="Open menu"
        onClick={onMenuClick}
      >
        ☰
      </button>
      <div className="flex items-center gap-2 md:gap-3 mr-2 md:mr-4">
        <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-brand-500 to-brand-700" />
        <span className="font-semibold text-slate-900 hidden sm:inline">
          Thynra Receptionist
        </span>
      </div>
      <BusinessSelector me={me} />
      <div className="flex-1" />
      <UserMenu me={me} onLogout={onLogout} />
    </header>
  );
}

function BusinessSelector({ me }: { me: Me }) {
  const qc = useQueryClient();
  // Operators with multiple businesses get a real selector. Tenants and
  // operators with only one business see a static label (no need for UI).
  if (me.user_kind === "business_user" || me.businesses.length <= 1) {
    return (
      <span className="hidden sm:inline text-sm text-slate-500 ml-2">
        ·{" "}
        {me.businesses.find((b) => b.id === me.active_business_id)?.name ??
          ""}
      </span>
    );
  }
  return (
    <select
      value={me.active_business_id ?? ""}
      onChange={async (e) => {
        await setActiveBusiness(e.target.value);
        qc.invalidateQueries({ queryKey: ["me"] });
        // Reset all section data when switching tenants — different business,
        // different rows. Cheapest reset = nuke the cache.
        qc.clear();
      }}
      className="ml-2 text-sm border border-slate-300 rounded-md px-2 py-1 bg-white"
    >
      {me.businesses.map((b) => (
        <option key={b.id} value={b.id}>
          {b.name}
        </option>
      ))}
    </select>
  );
}

function UserMenu({ me, onLogout }: { me: Me; onLogout: () => void }) {
  const [open, setOpen] = useState(false);
  const email =
    me.user_kind === "operator" ? me.operator.email : me.user.email;
  const role = me.user_kind === "operator" ? "Operator" : me.user.role;
  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-md hover:bg-slate-100 text-sm"
      >
        <span className="hidden sm:inline text-slate-700">{email}</span>
        <span className="text-xs text-slate-500 hidden sm:inline">·</span>
        <span className="text-xs text-slate-500">{role}</span>
        <span className="text-slate-400">▾</span>
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-1 w-48 bg-white border border-slate-200 rounded-lg shadow-lg z-10"
          onMouseLeave={() => setOpen(false)}
        >
          <button
            onClick={onLogout}
            className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-100"
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
